import { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { Icon, Input, Select, TextArea } from "semantic-ui-react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Classification, Schema } from "../../../../../../api";

import {
  initialValues,
  validationSchema,
} from "./EditClassificationPage.form";

import styles from "./EditClassificationPage.module.scss";

const classificationCtrl = new Classification();
const schemaCtrl = new Schema();

function getClassificationResponse(result) {
  return (
    result?.classification ||
    result?.response?.classification ||
    result?.response ||
    result
  );
}

function getSchemasResponse(result) {
  const response = result?.schemas || result?.response || result || {};

  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  return [];
}

function getClassificationsResponse(result) {
  const response =
    result?.classifications || result?.response || result || {};

  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  return [];
}

function getSchemaId(schema) {
  if (!schema) {
    return "";
  }

  if (typeof schema === "object") {
    return String(schema._id || schema.id || "");
  }

  return String(schema);
}

export function EditClassificationPage() {
  const { classificationId } = useParams();
  const navigate = useNavigate();

  const [classification, setClassification] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [schemaClassifications, setSchemaClassifications] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);
  const [serverError, setServerError] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Igual que en CreateCategoryPage/EditCategoryPage: mientras la
  // persona no toque "Orden" a mano, se le sigue sugiriendo el
  // siguiente disponible -- pero acá el valor inicial es el que ya
  // trae la clasificación, así que la sugerencia automática solo se
  // vuelve a aplicar cuando la persona CAMBIA el esquema (ver el
  // effect más abajo), nunca al cargar la página.
  const [orderTouchedByUser, setOrderTouchedByUser] = useState(false);
  const previousSchemaRef = useRef(null);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,

    onSubmit: async (values) => {
      try {
        setServerError("");

        const classificationData = {
          id: values.id.trim().toUpperCase(),
          name: values.name.trim(),
          description: values.description.trim(),
          schema: values.schema,
          order: Number(values.order),
        };

        await classificationCtrl.updateClassification(
          classificationId,
          classificationData
        );

        navigate("/admin/catalog/classification", {
          replace: true,
          state: {
            success: "Clasificación actualizada correctamente.",
          },
        });
      } catch (error) {
        console.error("Error al actualizar clasificación:", error);

        setServerError(
          error?.msg ||
            "No fue posible actualizar la clasificación. Intenta nuevamente."
        );

        if (error?.errors) {
          formik.setErrors(error.errors);
        }

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    },
  });

  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        setIsLoadingSchemas(true);
        setServerError("");

        const [classificationResult, schemasResult] = await Promise.all([
          classificationCtrl.getClassification(classificationId),
          schemaCtrl.getSchemas(1, 1000, {}),
        ]);

        const classificationResponse = getClassificationResponse(
          classificationResult
        );

        if (!classificationResponse?._id) {
          throw {
            msg: "No fue posible obtener la información de la clasificación.",
          };
        }

        const schemaDocs = getSchemasResponse(schemasResult);

        setSchemas(
          schemaDocs.map((schema) => ({
            key: schema._id,
            value: schema._id,
            text: `${schema.id} - ${schema.name}`,
          }))
        );

        setClassification(classificationResponse);

        formik.setValues({
          schema: getSchemaId(classificationResponse.schema),
          id: classificationResponse.id || "",
          name: classificationResponse.name || "",
          description: classificationResponse.description || "",
          order: classificationResponse.order ?? "",
        });
      } catch (errorResponse) {
        console.error("loadData error:", errorResponse);

        setClassification(null);

        setServerError(
          errorResponse?.msg ||
            "No fue posible cargar la clasificación. Intenta nuevamente."
        );
      } finally {
        setIsLoading(false);
        setIsLoadingSchemas(false);
      }
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classificationId]);

  // Clasificaciones del esquema seleccionado -- para sugerir el
  // siguiente "orden" disponible dentro de ese esquema. Se vuelve a
  // cargar cada vez que cambia el esquema elegido en el formulario.
  useEffect(() => {
    async function loadSchemaClassifications() {
      if (!formik.values.schema) {
        setSchemaClassifications([]);
        return;
      }

      try {
        const result = await classificationCtrl.getClassifications(1, 1000, {
          schema: formik.values.schema,
        });

        setSchemaClassifications(getClassificationsResponse(result));
      } catch (error) {
        console.error(
          "Error al cargar clasificaciones del esquema:",
          error
        );

        setSchemaClassifications([]);
      }
    }

    loadSchemaClassifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.schema]);

  // El orden solo compite entre clasificaciones del MISMO esquema --
  // excluye a la propia clasificación que se está editando (si no
  // aparecería como su propia "hermana" e inflaría la sugerencia).
  const siblingClassifications = useMemo(() => {
    return schemaClassifications.filter(
      (item) => String(item._id) !== String(classification?._id)
    );
  }, [schemaClassifications, classification]);

  const suggestedOrder = useMemo(() => {
    if (siblingClassifications.length === 0) {
      return 1;
    }

    const maxOrder = siblingClassifications.reduce(
      (max, item) => Math.max(max, Number(item.order) || 1),
      0
    );

    return maxOrder + 1;
  }, [siblingClassifications]);

  // Solo re-sugiere el orden cuando el esquema CAMBIA de verdad
  // (reasignar la clasificación a otro esquema vuelve obsoleto el
  // orden que traía, porque competía con otro grupo) -- nunca en la
  // carga inicial, donde previousSchemaRef todavía no tiene una base
  // con la cual comparar.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const schemaChanged =
      previousSchemaRef.current !== null &&
      previousSchemaRef.current !== formik.values.schema;

    previousSchemaRef.current = formik.values.schema;

    if (!schemaChanged || orderTouchedByUser) {
      return;
    }

    formik.setFieldValue("order", suggestedOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.schema, suggestedOrder, orderTouchedByUser, isLoading]);

  const getFieldError = (fieldName) => {
    const shouldShowError =
      (hasSubmitted || formik.touched[fieldName]) &&
      formik.errors[fieldName];

    return shouldShowError ? formik.errors[fieldName] : null;
  };

  const handleCancel = () => {
    if (formik.isSubmitting) {
      return;
    }

    navigate("/admin/catalog/classification");
  };

  const handleInputChange = (event, { name, value }) => {
    if (name === "order") {
      setOrderTouchedByUser(true);
    }

    formik.setFieldValue(name, value);

    if (hasSubmitted) {
      formik.validateField(name);
    }

    setServerError("");
  };

  const handleInputBlur = (event) => {
    formik.handleBlur(event);

    if (hasSubmitted) {
      formik.validateField(event.target.name);
    }
  };

  const handleSchemaBlur = () => {
    formik.setFieldTouched("schema", true);

    if (hasSubmitted) {
      formik.validateField("schema");
    }
  };

  const handleUseSuggestedOrder = () => {
    setOrderTouchedByUser(false);
    formik.setFieldValue("order", suggestedOrder);
  };

  const handleSave = async () => {
    setHasSubmitted(true);

    const touchedFields = {
      id: true,
      name: true,
      description: true,
      schema: true,
      order: true,
    };

    formik.setTouched(touchedFields, false);

    const validationErrors = await formik.validateForm();

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    formik.handleSubmit();
  };

  const isSaving = formik.isSubmitting;
  const isFormDisabled = isSaving || isLoadingSchemas;

  const showValidationError =
    hasSubmitted && Object.keys(formik.errors).length > 0;

  if (isLoading) {
    return (
      <section className={styles.EditClassificationPage}>
        <div className={styles.StateMessage}>Cargando clasificación...</div>
      </section>
    );
  }

  if (!classification) {
    return (
      <section className={styles.EditClassificationPage}>
        <div className={styles.ErrorState}>
          <p>{serverError || "Clasificación no encontrada."}</p>

          <Link
            to="/admin/catalog/classification"
            className={styles.BackToListButton}
          >
            Volver a clasificaciones
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.EditClassificationPage}>
      <header className={styles.PageHeader}>
        <div className={styles.TitleSection}>
          <button
            type="button"
            className={styles.BackButton}
            onClick={handleCancel}
            disabled={isSaving}
            aria-label="Volver al listado de clasificaciones"
          >
            <Icon name="arrow left" />
          </button>

          <div>
            <p className={styles.Breadcrumb}>
              Catálogo / Clasificaciones / {classification.name}
            </p>

            <h1>{classification.name}</h1>

            <p className={styles.Subtitle}>
              Actualiza los datos de esta clasificación.
            </p>
          </div>
        </div>

        <div className={styles.HeaderActions}>
          <button
            type="button"
            className={styles.CancelButton}
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancelar
          </button>

          <button
            type="button"
            className={styles.PublishButton}
            onClick={handleSave}
            disabled={isFormDisabled}
          >
            {isSaving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </header>

      {showValidationError && (
        <div className={styles.ValidationMessage}>
          <Icon name="warning sign" />

          <div>
            <strong>Revisa la información de la clasificación.</strong>

            <span>
              Debes corregir los campos obligatorios antes de guardar los
              cambios.
            </span>
          </div>
        </div>
      )}

      {serverError && (
        <div className={styles.ServerErrorMessage}>
          <Icon name="warning sign" />
          <span>{serverError}</span>
        </div>
      )}

      <form
        className={styles.FormContent}
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <section className={styles.ClassificationBasicInformation}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Información de la clasificación</h2>

              <p>
                Define el esquema padre y los datos del valor que deseas
                actualizar.
              </p>
            </div>
          </header>

          <div className={styles.FormGrid}>
            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="schema">
                Esquema <span>*</span>
              </label>

              <Select
                id="schema"
                name="schema"
                fluid
                search
                selection
                options={schemas}
                value={formik.values.schema}
                placeholder={
                  isLoadingSchemas
                    ? "Cargando esquemas..."
                    : "Selecciona un esquema"
                }
                error={Boolean(getFieldError("schema"))}
                loading={isLoadingSchemas}
                disabled={isFormDisabled}
                noResultsMessage="No se encontraron esquemas."
                onChange={handleInputChange}
                onBlur={handleSchemaBlur}
              />

              {getFieldError("schema") && (
                <span className={styles.FieldError}>
                  {getFieldError("schema")}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="id">
                Código de la clasificación <span>*</span>
              </label>

              <Input
                id="id"
                name="id"
                value={formik.values.id}
                placeholder="Ej. BOLSAS"
                error={Boolean(getFieldError("id"))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isFormDisabled}
              />

              {getFieldError("id") && (
                <span className={styles.FieldError}>
                  {getFieldError("id")}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="order">
                Orden de visualización <span>*</span>
              </label>

              <Input
                id="order"
                name="order"
                type="number"
                min="1"
                value={formik.values.order}
                placeholder="Ej. 1"
                error={Boolean(getFieldError("order"))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isFormDisabled}
              />

              {getFieldError("order") ? (
                <span className={styles.FieldError}>
                  {getFieldError("order")}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  Siguiente disponible en este esquema: {suggestedOrder}.{" "}
                  {Number(formik.values.order) !== suggestedOrder && (
                    <button
                      type="button"
                      className={styles.UseSuggestedOrderButton}
                      onClick={handleUseSuggestedOrder}
                      disabled={isFormDisabled}
                    >
                      Usar {suggestedOrder}
                    </button>
                  )}
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="name">
                Nombre de la clasificación <span>*</span>
              </label>

              <Input
                id="name"
                name="name"
                value={formik.values.name}
                placeholder="Ej. Bolsas"
                error={Boolean(getFieldError("name"))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isFormDisabled}
              />

              {getFieldError("name") && (
                <span className={styles.FieldError}>
                  {getFieldError("name")}
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="description">Descripción</label>

              <TextArea
                id="description"
                name="description"
                value={formik.values.description}
                placeholder="Ej. Productos que corresponden a la categoría de bolsas."
                rows={5}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isFormDisabled}
              />

              <div className={styles.TextAreaFooter}>
                {getFieldError("description") ? (
                  <span className={styles.FieldError}>
                    {getFieldError("description")}
                  </span>
                ) : (
                  <span />
                )}

                <span>{formik.values.description.length}/500</span>
              </div>
            </div>
          </div>
        </section>
      </form>
    </section>
  );
}
